// Admin: Partners management — list / add / edit / soft-toggle / delete with stats.
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage } from './_shared';
import PartnerForm from './PartnerForm';

export default function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/admin/partners');
      setPartners(r.data);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load partners'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (form) => {
    try {
      await axiosInstance.post('/admin/partners', form);
      toast.success('Partner created');
      setView('list');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const updateOne = async (form) => {
    try {
      await axiosInstance.put(`/admin/partners/${editing.id}`, form);
      toast.success('Partner updated');
      setView('list');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const toggleActive = async (p) => {
    try {
      await axiosInstance.put(`/admin/partners/${p.id}`, { is_active: !p.is_active });
      toast.success(p.is_active ? 'Partner deactivated' : 'Partner activated');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const remove = async (p) => {
    const warn =
      p.services_count > 0
        ? `\n\n⚠ This partner has ${p.services_count} service(s) and ${p.bookings_count} booking(s). They will be detached (kept but unlinked).\n\nConsider deactivating instead.`
        : '';
    if (!window.confirm(`Delete "${p.name}"?${warn}`)) return;
    try {
      await axiosInstance.delete(`/admin/partners/${p.id}`);
      toast.success('Partner deleted');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const resyncCatalogue = async (p) => {
    if (
      !window.confirm(
        `Re-sync ${p.name}'s catalogue from the registered manifest?\n\n` +
          `This will overwrite name/description/images/prices/variants on existing services ` +
          `(matched by name) and add any new ones. Custom edits to those fields will be lost.`,
      )
    )
      return;
    try {
      const r = await axiosInstance.post(`/admin/partners/${p.slug}/import`);
      toast.success(
        `Synced. created=${r.data.created} updated=${r.data.updated} total=${r.data.total}`,
      );
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Re-sync failed'));
    }
  };

  if (view === 'create')
    return (
      <div>
        <button
          onClick={() => setView('list')}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4"
        >
          <i className="ph-bold ph-arrow-left" /> Back
        </button>
        <h2 className="text-lg font-extrabold mb-4">New Partner</h2>
        <PartnerForm onSave={create} onCancel={() => setView('list')} />
      </div>
    );

  if (view === 'edit' && editing)
    return (
      <div>
        <button
          onClick={() => {
            setView('list');
            setEditing(null);
          }}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4"
        >
          <i className="ph-bold ph-arrow-left" /> Back
        </button>
        <h2 className="text-lg font-extrabold mb-4">Edit Partner</h2>
        <PartnerForm
          initial={editing}
          onSave={updateOne}
          onCancel={() => {
            setView('list');
            setEditing(null);
          }}
        />
      </div>
    );

  return (
    <div className="space-y-4">
      <button
        onClick={() => setView('create')}
        data-testid="admin-add-partner-btn"
        className="w-full py-3 bg-[#2B3A67] text-white font-extrabold rounded-2xl flex items-center justify-center gap-2"
      >
        <i className="ph-bold ph-plus" /> Add New Partner
      </button>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" />
        </div>
      ) : partners.length === 0 ? (
        <div className="text-center text-gray-400 py-10 text-sm">
          No partners yet. Add your first to start onboarding services.
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-partners-list">
          {partners.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-partner-row-${p.slug}`}
              className={`bg-white rounded-2xl p-4 shadow-sm border ${
                p.is_active ? 'border-gray-100' : 'border-gray-200 opacity-70'
              }`}
            >
              <div className="flex items-start gap-3">
                {p.logo_url ? (
                  <img
                    src={p.logo_url}
                    alt={p.name}
                    className="w-12 h-12 rounded-xl object-cover shrink-0 border border-gray-100"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                    <i className="ph-bold ph-handshake text-xl" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-extrabold truncate">{p.name}</p>
                    {!p.is_active && (
                      <span className="text-[10px] font-black bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full uppercase">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate">
                    /{p.slug} · {p.contact_email || 'no email'}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs">
                    <span className="font-bold text-[#2B3A67]">
                      <i className="ph-bold ph-storefront" /> {p.services_count} services
                    </span>
                    <span className="font-bold text-[#E54562]">
                      <i className="ph-bold ph-calendar-check" /> {p.bookings_count} bookings
                    </span>
                  </div>
                  {p.available_cities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.available_cities.map((c) => (
                        <span
                          key={c}
                          className="text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded-full text-gray-600"
                        >
                          📍 {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                <button
                  onClick={() => {
                    setEditing(p);
                    setView('edit');
                  }}
                  className="flex-1 min-w-[80px] px-3 py-2 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs rounded-xl"
                  data-testid={`edit-partner-${p.slug}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleActive(p)}
                  className={`flex-1 min-w-[80px] px-3 py-2 border-2 font-bold text-xs rounded-xl ${
                    p.is_active
                      ? 'border-amber-500 text-amber-600'
                      : 'border-emerald-500 text-emerald-600'
                  }`}
                  data-testid={`toggle-partner-${p.slug}`}
                >
                  {p.is_active ? 'Deactivate' : 'Activate'}
                </button>
                {p.has_catalogue && (
                  <button
                    onClick={() => resyncCatalogue(p)}
                    className="flex-1 min-w-[110px] px-3 py-2 border-2 border-[#E54562] text-[#E54562] font-bold text-xs rounded-xl flex items-center justify-center gap-1"
                    data-testid={`resync-partner-${p.slug}`}
                    title="Re-sync this partner's catalogue from the registered manifest"
                  >
                    <i className="ph-bold ph-arrows-clockwise" />
                    Re-sync
                  </button>
                )}
                <button
                  onClick={() => remove(p)}
                  className="px-3 py-2 border-2 border-[#E54562] text-[#E54562] font-bold text-xs rounded-xl"
                  data-testid={`delete-partner-${p.slug}`}
                >
                  <i className="ph-bold ph-trash" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
